import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatCommandsApi } from '@/api/chatCommands'
import { useTenantKey } from './useTenantKey'
import type { ExecuteChatCommandRequest } from '@/types'

export function useChatCommands(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'chat-commands'],
    queryFn: () => chatCommandsApi.getCommands(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useExecuteChatCommand(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (req: ExecuteChatCommandRequest) =>
      chatCommandsApi.execute(projectId, req),
    onSuccess: (_, req) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', req.chatId, 'messages') })
    },
  })
}

export function useAvailableWorkflowsForChat(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'workflows', 'available-for-chat'],
    queryFn: () => chatCommandsApi.getAvailableWorkflowsForChat(projectId),
    enabled: !!projectId,
    staleTime: 60 * 1000,
  })
}

export function useTriggerWorkflowFromChat(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ workflowName, chatId, inputValuesJson }: { workflowName: string; chatId: string; inputValuesJson?: string }) =>
      chatCommandsApi.triggerWorkflowFromChat(projectId, workflowName, { chatId, inputValuesJson }),
    onSuccess: (_, req) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', req.chatId, 'messages') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}
